/**
 * Driver API
 *
 * API endpoints for driver management operations
 */

import dayjs from "dayjs";
import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import { queueApi } from "../queue/queue.api";
import type {
  Driver,
  DriverGroup,
  DriverGroupArgs,
  Drivers,
  DriversGroup,
  DriversGroupTransformed,
  EditDriverArgs,
  ListDriver,
  ListDriverGroup,
  ListDriverGroupTransformed,
  SearchDriver
} from "./driver.types";
import { DriverGroupArgsSchema, EditDriverArgsSchema } from "./schemas";

/**
 * Driver API endpoints
 * Provides CRUD operations, group queries, and search functionality for driver management
 */
export const driverApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get drivers group with queue position integration
     * @param args - Filter parameters including status, search terms, company and shipping company IDs
     * @returns Array of drivers with queue status and position
     * @deprecated
     */
    getDriversGroup: build.query<DriversGroupTransformed[], DriverGroupArgs, DriversGroup[]>({
      query: ({ status = "Nenhum", driver_search = undefined, truck_search = undefined, ...params }) => {
        const statusFormatted = status === "Nenhum" ? undefined : Number(status);

        return {
          url: "/driver/groups",
          params: {
            ...params,
            page_size: 50,
            driver_search,
            truck_search,
            status: statusFormatted
          }
        };
      },
      argSchema: DriverGroupArgsSchema,
      onQueryStarted: async (args, { queryFulfilled, dispatch, updateCachedData }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando motoristas...",
          success: "Motoristas carregados com sucesso!",
          error: "Erro ao carregar motoristas"
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let patchCollection: any;

        try {
          const { data } = await queryFulfilled;
          const ids = data.map((driver) => driver.id);
          const queueDriverPositions = await dispatch(queueApi.endpoints.getQueueDriverPositions.initiate({ ids })).unwrap();

          patchCollection = updateCachedData((draft) => {
            draft.forEach((driver) => {
              const driverPosition = queueDriverPositions.find((position) => position.id === driver.id);
              if (driverPosition) {
                Object.assign(driver, { $position: driverPosition.position });
              }
            });
          });
        } catch {
          // Se alguma requisição falhar, desfaz as mudanças no cache se o patchCollection estiver disponível
          if (patchCollection) {
            patchCollection.undo();
          }
        }
      },
      transformResponse: (response) => {
        const transformedRes = response.map((driver) => {
          const driverHasOrder = driver.queue[0]?.id ? 1 : 0;

          return {
            ...driver,
            queueStatus: driverHasOrder
          };
        });

        return transformedRes;
      }
    }),

    /**
     * List drivers groups with pagination (infinite query)
     * @param params - Filter and pagination parameters
     * @returns Paginated list of drivers groups
     */
    listDriversGroup: build.infiniteQuery<ListDriverGroupTransformed, DriverGroupArgs, App.Filters.Pagination<DriverGroup, { filter?: string }>, ListDriverGroup>({
      infiniteQueryOptions: {
        initialPageParam: { page: 0, pageSize: 50, sortDirection: "asc", sortField: "name" },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/driver/groups",
        params: { ...pageParam, ...queryArg }
      }),
      // @ts-expect-error Problema de tipo
      transformResponse: (response) => {
        const { data, ...rest } = response;

        const resultWithQueueStatus = data.map((item) => {
          const queueStatus = item.queue && item.queue[0]?.id ? 1 : 0;

          return {
            ...item,
            queueStatus
          };
        });

        return {
          ...rest,
          data: resultWithQueueStatus
        };
      },
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando motoristas...",
          success: "Motoristas carregados com sucesso!",
          error: "Erro ao carregar motoristas"
        });
      }
    }),

    /**
     * Get all drivers
     * @returns Array of drivers with formatted birth dates
     */
    getDrivers: build.query<Drivers[], void, Drivers[]>({
      query: () => "/driver",
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando motoristas...",
          success: "Motoristas carregados com sucesso!",
          error: "Erro ao carregar motoristas"
        });
      },
      transformResponse: (response) => {
        const transformedResponse = response.map((driver) => ({
          ...driver,
          birthDate: dayjs(driver.birthDate).format("DD/MM/YYYY")
        }));

        return transformedResponse;
      }
    }),

    /**
     * Get a single driver by ID
     * @param id - Driver ID
     * @returns Full driver details with formatted birth date
     */
    getDriver: build.query<Driver, Pick<Driver, "id">, Driver>({
      query: ({ id }) => `/driver/${id}`,
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando motorista...",
          success: "Motorista carregado com sucesso!",
          error: "Erro ao carregar motorista"
        });
      },
      transformResponse: (response) => ({
        ...response,
        birthDate: dayjs(response.birthDate).format("DD/MM/YYYY")
      })
    }),

    /**
     * Unbind driver from shipping company
     * @param id - Driver ID
     * @returns void
     */
    unbindDriverShippingCompany: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: "/driver/unbind-shipping-company",
        body: {
          driver: {
            id
          }
        },
        method: "PATCH"
      })
    }),

    /**
     * Create or update a driver
     * @param payload - Driver data (id = null for create, id > 0 for update)
     * @returns Updated driver data
     */
    editDriver: build.mutation<Driver, EditDriverArgs>({
      query: ({ company, shippingCompany, truck, ...body }) => {
        return {
          url: "/driver/save",
          method: "POST",
          body: {
            ...body,
            company: company?.id
              ? {
                  id: Number(company?.id)
                }
              : undefined,
            shippingCompany: shippingCompany?.id
              ? {
                  id: Number(shippingCompany?.id)
                }
              : undefined,
            truck: truck?.map(({ id, vehicleModel, brand: _brand, driver, company, shippingCompany, ...truckBody }) => ({
              ...truckBody,
              id: id === null || id === undefined || id === "" ? null : Number(id),
              vehicleModel: vehicleModel?.id
                ? {
                    id: Number(vehicleModel.id)
                  }
                : undefined,
              driver: driver?.id
                ? {
                    id: Number(driver.id)
                  }
                : undefined,
              company: company?.id
                ? {
                    id: Number(company.id)
                  }
                : undefined,
              shippingCompany: shippingCompany?.id
                ? {
                    id: Number(shippingCompany.id)
                  }
                : undefined
            }))
          }
        };
      },
      argSchema: EditDriverArgsSchema,
      onQueryStarted: ({ id }, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: id ? "Editando motorista..." : "Cadastrando motorista...",
          success: id ? "Motorista editado com sucesso!" : "Motorista cadastrado com sucesso!",
          error: () => {
            if (id) return "Erro ao editar motorista";

            return "Erro ao cadastrar motorista";
          }
        });
      }
    }),

    /**
     * Delete a driver
     * @param id - Driver ID to delete
     * @returns void
     */
    deleteDriver: build.mutation<void, Pick<Driver, "id">>({
      query: ({ id }) => ({
        url: `/driver/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo motorista...",
          success: "Motorista excluído com sucesso!",
          error: (err) => {
            const error = err.error?.data?.message;
            if (!error) return "Erro desconhecido!";

            return error;
          }
        });
      }
    }),

    /**
     * List drivers with pagination (infinite query)
     * @param params - Filter and pagination parameters
     * @returns Paginated list of drivers with formatted birth dates
     */
    listDrivers: build.infiniteQuery<ListDriver, { id?: number; params?: App.Filters.Base<Driver> }, App.Filters.Pagination<Driver, { companyId?: number }>, ListDriver>({
      infiniteQueryOptions: {
        initialPageParam: { page: 0, pageSize: 20, sortDirection: "asc", sortField: "name" },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg: { params }, pageParam }) => ({
        url: "/driver",
        params: Object.assign({}, pageParam, params)
      }),
      transformResponse: (response) => {
        const transformedData = response.data.map((driver) => ({
          ...driver,
          birthDate: dayjs(driver.birthDate, "YYYY-MM-DD").format("DD/MM/YYYY")
        }));

        return {
          ...response,
          data: transformedData
        };
      },
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando motoristas...",
          success: "Motoristas carregados com sucesso!",
          error: "Erro ao carregar motoristas"
        });
      }
    }),

    /**
     * Search drivers by name
     * @param name - Search query string
     * @returns Array of matching drivers
     */
    searchDrivers: build.query<SearchDriver[], Pick<SearchDriver, "name">>({
      query: ({ name }) => ({
        url: `/driver/like/${name}`
      })
      // onQueryStarted: (args, { queryFulfilled }) => {
      //   toast.promise(
      //     queryFulfilled,
      //     {
      //       loading: "Buscando motoristas...",
      //       success: "Motoristas encontrados com sucesso!",
      //       error: "Erro ao buscar motoristas"
      //     },
      //     {
      //       toasterId: "search-driver-toast"
      //     }
      //   );
      // }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
driverApi.enhanceEndpoints({
  endpoints: {
    getDriversGroup: {
      providesTags: (result) => {
        if (!result) return [{ type: "DriverGroup", id: "LIST" }];

        return [...result.map(({ id }) => ({ type: "DriverGroup" as const, id })), { type: "DriverGroup", id: "LIST" }];
      }
    },
    getDriver: {
      providesTags: (result, err, { id }) => [{ type: "Driver", id }]
    },
    getDrivers: {
      providesTags: (response) => (response ? [...response.map(({ id }) => ({ type: "Driver" as const, id })), { type: "Driver", id: "LIST" }] : [{ type: "Driver", id: "LIST" }])
    },
    unbindDriverShippingCompany: {
      invalidatesTags: (result, err) => (!err ? ["DriverGroup"] : [])
    },
    editDriver: {
      invalidatesTags: (result, err, { id }) =>
        !err
          ? [
              { type: "Driver", id: id ?? "LIST" },
              { type: "DriverGroup", id: id ?? "LIST" }
            ]
          : []
    },
    deleteDriver: {
      invalidatesTags: (result, err, { id }) => (!err ? [{ type: "Driver", id }] : [{ type: "Driver", id: "LIST" }])
    },
    listDrivers: {
      providesTags: (response) =>
        response
          ? [...response.pages.flatMap((page) => page.data.map(({ id }) => ({ type: "Driver" as const, id }))), { type: "Driver", id: "LIST" }]
          : [{ type: "Driver", id: "LIST" }]
    },
    searchDrivers: {
      providesTags: (response) => (response ? [...response.map(({ id }) => ({ type: "Driver" as const, id })), { type: "Driver", id: "LIST" }] : [{ type: "Driver", id: "LIST" }])
    }
  }
});
